/**
 * SRV-FG-5-1: Disassembly Protocol Modal
 * Allows disassembling completed BERGEP worksheets into parts for inventory
 */

import { type DisassemblyPart, executeDisassembly } from '@/api/worksheets';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useTranslation } from '@kgc/i18n';
import { useState } from 'react';

interface DisassemblyModalProps {
  worksheetId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CONDITION_OPTIONS = ['NEW', 'USED', 'DAMAGED'] as const;

export function DisassemblyModal({
  worksheetId,
  isOpen,
  onClose,
  onSuccess,
}: DisassemblyModalProps) {
  const { t } = useTranslation();
  const [parts, setParts] = useState<DisassemblyPart[]>([
    { productId: '', quantity: 1, condition: 'USED' },
  ]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleAddPart = () => {
    setParts([...parts, { productId: '', quantity: 1, condition: 'USED' }]);
  };

  const handleRemovePart = (index: number) => {
    setParts(parts.filter((_, i) => i !== index));
  };

  const handlePartChange = (
    index: number,
    field: keyof DisassemblyPart,
    value: string | number
  ) => {
    const updated = [...parts];
    const part = updated[index];
    if (!part) return;
    if (field === 'quantity') {
      part.quantity = typeof value === 'number' ? value : parseInt(String(value), 10) || 1;
    } else if (field === 'condition') {
      part.condition = value as DisassemblyPart['condition'];
    } else {
      part.productId = String(value);
    }
    setParts(updated);
  };

  const handleExecute = async () => {
    setError(null);

    // Validate
    const validParts = parts.filter(p => p.productId.trim().length > 0);
    if (validParts.length === 0) {
      setError('At least one part with a product ID is required');
      return;
    }

    setIsExecuting(true);
    try {
      await executeDisassembly(worksheetId, validParts);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('service.disassembly.error'));
    } finally {
      setIsExecuting(false);
    }
  };

  const getConditionLabel = (condition: string): string => {
    switch (condition) {
      case 'NEW':
        return t('service.disassembly.conditionNew');
      case 'USED':
        return t('service.disassembly.conditionUsed');
      case 'DAMAGED':
        return t('service.disassembly.conditionDamaged');
      default:
        return condition;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardContent className="p-6">
          <h2 className="text-xl font-bold mb-1">{t('service.disassembly.modalTitle')}</h2>
          <p className="text-sm text-muted-foreground mb-6">
            {t('service.disassembly.modalDescription')}
          </p>

          {/* Parts list */}
          <div className="space-y-3 mb-4">
            {parts.map((part, index) => (
              <div
                key={index}
                className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
              >
                <Input
                  className="flex-1"
                  placeholder={t('service.disassembly.productSearch')}
                  value={part.productId}
                  onChange={e => handlePartChange(index, 'productId', e.target.value)}
                />
                <Input
                  className="w-20"
                  type="number"
                  min={1}
                  value={part.quantity}
                  onChange={e => handlePartChange(index, 'quantity', e.target.value)}
                />
                <select
                  className="rounded-md border border-gray-300 dark:border-gray-600 bg-background px-3 py-2 text-sm"
                  value={part.condition}
                  onChange={e => handlePartChange(index, 'condition', e.target.value)}
                >
                  {CONDITION_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>
                      {getConditionLabel(opt)}
                    </option>
                  ))}
                </select>
                {parts.length > 1 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600"
                    onClick={() => handleRemovePart(index)}
                  >
                    X
                  </Button>
                )}
              </div>
            ))}
          </div>

          <Button variant="outline" size="sm" onClick={handleAddPart} className="mb-4">
            + {t('service.disassembly.addPart')}
          </Button>

          {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isExecuting}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleExecute} disabled={isExecuting}>
              {isExecuting ? t('service.disassembly.executing') : t('service.disassembly.execute')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
